import type { TimeSeries } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import {
  findPointAfterIndex,
  findPointAtOrAfterIndex,
} from '@/shared/charts/time-series-chart/model/findNearestTimeSeriesPoint';

import { symbolVisibility } from '../../config/chartVisuals';
import type { LineViewport } from './lineViewport';

type SymbolCandidate = {
  id: string;
  visiblePointCount: number;
};

/**
 * The lines that show their symbols in the given viewport. Each line qualifies on its own: its
 * visible samples must be, on average, at least `minPixelsPerPoint` apart. If the qualifying lines
 * together would draw more than `maxVisibleSymbols`, the densest ones give up their symbols first,
 * so the symbol cost stays bounded however many lines are selected.
 *
 * Two binary searches per line, so it is cheap enough to run on every option build.
 */
export function getSymbolSeriesIds(series: TimeSeries[], viewport: LineViewport): Set<string> {
  const [start, end] = viewport.visibleRange;
  const maxPointsPerLine = Math.floor(viewport.plotWidthPx / symbolVisibility.minPixelsPerPoint);

  const candidates: SymbolCandidate[] = series.flatMap(({ id, points }) => {
    const visiblePointCount = findPointAfterIndex(points, end) - findPointAtOrAfterIndex(points, start);

    return visiblePointCount > 0 && visiblePointCount <= maxPointsPerLine ? [{ id, visiblePointCount }] : [];
  });

  // Sparsest first: when the total cap bites, the densest lines are the ones left without symbols.
  candidates.sort((first, second) => first.visiblePointCount - second.visiblePointCount);

  const symbolSeriesIds = new Set<string>();
  let symbolCount = 0;

  for (const { id, visiblePointCount } of candidates) {
    if (symbolCount + visiblePointCount > symbolVisibility.maxVisibleSymbols) {
      break;
    }

    symbolSeriesIds.add(id);
    symbolCount += visiblePointCount;
  }

  return symbolSeriesIds;
}
